import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Auth, Hub } from 'aws-amplify';
import { ApolloProvider } from '@apollo/client';
import Router from './Router';
import SessionExpiredModal from 'components/SessionExpiredModal';
import { AlertProvider } from 'components/message/AlertContext';
import { RecentlyViewedProvider } from 'views/HomePage/recentlyViewed/RecentlyViewedProvider';
import AlertSnackbar from 'components/message/AlertSnackbar';
import Sidebar from 'components/sidebar/Sidebar';
import AuthView from 'views/Auth';
import clsx from 'clsx';
import './authConfig';
import { useInitClient } from 'api/client';
import Spinner from 'components/loader/Spinner';
import { ModalContextProvider } from 'hooks/useModal';
import SidebarMenuIcon from 'assets/icons/white/SidebarMenuIcon';
import { NTooltip } from '@innersource-nn/novoui-react';

const App = () => {
    const [user, setUser] = useState<string | null>(null);
    const client = useInitClient();
    const [closeSidebar, setClosedSidebar] = useState(window.innerWidth < 1280);

    Hub.listen('auth', ({ payload: { event, data } }) => {
        switch (event) {
            case 'signIn':
                const tokenData = data.signInUserSession.idToken.payload;
                setUser(tokenData.email || tokenData['cognito:username']);
                break;
            case 'oAuthSignOut':
            case 'signOut':
                setUser(null);
                localStorage.clear();
                break;
            case 'signIn_failure':
                console.log('Sign in failure');
                break;
            default:
                break;
        }
    });

    useEffect(() => {
        Auth.currentAuthenticatedUser()
            .then((userData) => {
                setUser(userData.attributes.email || userData.username);
            })
            .catch(() => {
                setUser(null);
                localStorage.clear();
                console.log('User not authorized');
            });
    }, []);

    return (
        <div className="flex h-screen">
            <BrowserRouter>
                <AlertProvider>
                    {!user ? (
                        <AuthView />
                    ) : client ? (
                        <ApolloProvider client={client}>
                            <ModalContextProvider>
                                <RecentlyViewedProvider>
                                    <Sidebar
                                        email={user}
                                        closeSidebar={closeSidebar}
                                    />
                                    <div className="flex bg-faded-blue-100 size-full">
                                        <span>
                                            <NTooltip
                                                className="pb-12"
                                                content={
                                                    closeSidebar
                                                        ? 'Expand sidebar'
                                                        : 'Collapse sidebar'
                                                }
                                                key="sidebar-tooltip"
                                                placement="right"
                                            >
                                                <SidebarMenuIcon
                                                    className={clsx(
                                                        !closeSidebar &&
                                                            'rotate-180',
                                                        'fill-faded-blue-600 hover:fill-sea-blue-600 w-24 mt-16 ml-16 cursor-pointer self-start',
                                                    )}
                                                    onClick={() =>
                                                        setClosedSidebar(
                                                            !closeSidebar,
                                                        )
                                                    }
                                                />
                                            </NTooltip>
                                        </span>

                                        <div className="px-48 pb-48 overflow-auto w-full ">
                                            <Router />
                                            <SessionExpiredModal />
                                        </div>
                                    </div>
                                </RecentlyViewedProvider>
                            </ModalContextProvider>
                        </ApolloProvider>
                    ) : (
                        <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
                            <Spinner />
                        </div>
                    )}
                    <AlertSnackbar />
                </AlertProvider>
            </BrowserRouter>
        </div>
    );
};

export default App;
